import { i18n } from '@shared/i18n'

import apolloClient from '@/graphql/client';
import createTeamRegistrationMutation from '@/graphql/mutations/createTeamRegistration';
import generateTeamRegistrationIdQuery from '@/graphql/queries/generateTeamRegistrationId';

const INITIAL_MODEL = {
  id: null,
  property: null,
  subproperty: null,
  clubLogo: null,
  clubName: null,
  clubLegalName: null,
  clubType: null,
  clubFederation: null,
  clubLegalEntityId: null,
  clubWebsite: null,
  clubNeedsAccommodation: false,
  clubKitHome: null,
  clubKitAway: null,
  clubAddress: {
    country: 'ES'
  },
  clubContactPersons: null,
  clubCategories: null,
  clubTeams: null,
}

export default {
  namespaced: true,
  state: {
    isLoading: false,
    model: { ...INITIAL_MODEL },
  },
  getters: {
    model: state => state.model,
    clubName: state => state.model.clubName,
    clubCategoriesInUseByGenderAndFormat: state => state.model.clubCategories && state.model.clubCategories.reduce((result, category) => {
      const { gender, format, ageCategory } = category
      if (gender && format && ageCategory) {
        if (!result[gender][format]) result[gender][format] = []
        result[gender][format].push(ageCategory)
      }
      return result
    }, {
      male: {},
      female: {},
    })
  },
  mutations: {
    clear(state) {
      state.isLoading = false
      state.model.clubContactPersons = { ...INITIAL_MODEL.clubContactPersons }
      state.model.clubCategories = { ...INITIAL_MODEL.clubCategories }
      state.model.clubTeams = null
      state.model = { ...INITIAL_MODEL }
    },
    setLoading(state, payload = true) {
      state.isLoading = payload
    },
    set(state, { key, val }) {
      if (!key) return
      state.model = {
        ...state.model,
        [key]: val,
      }
    },
  },
  actions: {
    async fetchNewId({ commit }) {
      const { data: { teamRegistrationGenerateId: newId } } = await apolloClient.query({ query: generateTeamRegistrationIdQuery })
      commit('set', {
        key: 'id',
        val: newId,
      })
    },
    async submit({ commit, getters }) {
      try {
        commit('setLoading', true)
        
        const teamRegistration = {
          ...getters.model,
          clubLogo: getters.model.clubLogo[0],
        }
        delete teamRegistration.clubCategories
  
        await apolloClient.mutate({
          mutation: createTeamRegistrationMutation,
          variables: { teamRegistration },
        })
      } finally {
        commit('setLoading', false)
      }
    },
    parseCategoriesFromTeams({ state }) {
      if (!state.model.clubTeams) return

      state.model.clubCategories = Object.values(state.model.clubTeams.reduce((result, { gender, format, ageCategory, name }) => {
        const key = `${gender}-${format}-${ageCategory}`
        if (!(key in result)) result[key] = {
          gender,
          format,
          ageCategory,
          minimumPlayersCommitment: true,
          multipleTeams: false,
        }
        if (name) {
          if (!('teams' in result[key])) result[key].teams = []
          result[key].teams.push(name)
          result[key].multipleTeams = true
        }
        return result
      }, {}))

      if (!state.model.clubCategories.length) state.model.clubCategories.push({})
    },
    parseTeamsFromCategories({ state, rootState }) {
      if (!state.model.clubCategories) return

      state.model.clubTeams = state.model.clubCategories.reduce((result, { gender, format, ageCategory, multipleTeams, teams }) => {
        if (!gender || !format || !ageCategory) return result

        const { clubName } = state.model
        const i18nGender = i18n(`enumerators.gender.${gender}`)
        const i18nFormat = i18n(`enumerators.soccerFormat.${format}`)
        const i18nAgeCategory = rootState.tournament.ageCategories.find(c => c.id === ageCategory).label

        const teamName = `${clubName} - ${i18nGender} - ${i18nFormat} - ${i18nAgeCategory}`

        if (multipleTeams) {
          for (const team of teams) {
            result.push({
              gender,
              format,
              ageCategory,
              name: team,
              fullName: `${teamName} - ${team}`,
            })
          }
        } else {
          result.push({
            gender,
            format,
            ageCategory,
            name: '',
            fullName: teamName,
          })
        }
        return result
      }, [])
    },
  },
}
